import {
  AlignmentType,
  BorderStyle,
  Document,
  ExternalHyperlink,
  Footer,
  Header,
  HeadingLevel,
  HighlightColor,
  ImageRun,
  LevelFormat,
  LineRuleType,
  Packer,
  PageBreak,
  PageNumber,
  Paragraph,
  SectionType,
  Tab,
  TabStopType,
  TextRun,
  UnderlineType,
  type ISectionOptions,
  type ParagraphChild,
} from "docx";
import type { EditorFontId } from "../editor/fonts";
import { fontStackFor } from "../editor/fonts";
import {
  isJust,
  isNothing,
  just,
  nothing,
  type Maybe,
  toMaybe,
} from "../lib/maybe";
import { assertNever } from "../lib/assertNever";
import { isTauri } from "../lib/runtime";
import { trimmedMaybe } from "../lib/trimmedString";
import { readProjectAssetDataUrl } from "../lib/tauri/assets";
import type {
  ZwChapterHeadingStyle,
  ZwCopyrightPage,
  ZwDedicationPage,
  ZwMetadata,
  ZwPageChrome,
  ZwParagraphFormatting,
  ZwPrintSize,
} from "../lib/zwConfig";
import {
  defaultZwCopyrightPage,
  defaultZwDedicationPage,
  defaultZwMetadata,
  defaultZwPageChrome,
} from "../lib/zwConfig";
import { chaptersFromBlocks, type BookBlock } from "./buildBook";
import {
  chapterTitleDocxHalfPoints,
  chapterTitleSpacingTwips,
  resolveChapterHeadingStyle,
} from "./chapterHeading";
import { buildCopyrightPageHtml } from "./copyrightPage";
import { buildDedicationPageHtml } from "./dedicationPage";
import { assetRelativeFromSrc, decodeDataUrl } from "./exportAssets";
import {
  DEFAULT_MAP_EXPORT_DPI,
  fitPreparedMapForPrintBox,
  mapImagesForPrint,
  type MapExportImage,
} from "./mapFigureExport";
import { PARAGRAPH_INDENT_EM, RAISED_INITIAL_EM } from "./paragraphStyle";
import { cssLengthToTwip, printPageSizeFor } from "./printSizes";

export type BuildDocxInput = {
  title: string;
  subtitle?: string;
  author: string;
  printSize?: ZwPrintSize;
  pageChrome?: ZwPageChrome;
  paragraphFormatting: ZwParagraphFormatting;
  chapterHeadingStyle?: ZwChapterHeadingStyle;
  copyrightPage?: ZwCopyrightPage;
  dedicationPage?: ZwDedicationPage;
  metadata?: ZwMetadata;
  bodyFontId: EditorFontId;
  headingFontId: EditorFontId;
  rootPath: string;
  /** Manuscript order: chapters and sequence map figures. */
  blocks: BookBlock[];
};

type RunStyle = {
  bold?: boolean;
  italics?: boolean;
  underline?: boolean;
  strike?: boolean;
  highlight?: boolean;
  code?: boolean;
  link?: string;
  /** Chapter-opening lead-in. */
  smallCaps?: boolean;
  /** Chapter-opening initial: Word has no drop cap without a frame, so both
   * drop cap and raised initial export as an enlarged first letter. */
  initial?: boolean;
};

/** Copyright / dedication pages keep their own spaced layout. */
const FRONT_MATTER_PARAGRAPHS: ZwParagraphFormatting = {
  style: "spaced",
  firstParagraph: "indented",
  openingInitial: "none",
  openingLeadIn: "none",
};

type Fonts = {
  body: string;
  heading: string;
  mono: string;
};

function primaryFontName(id: EditorFontId): string {
  const stack = fontStackFor(id);
  const quoted = toMaybe(
    stack.match(/^"([^"]+)"/) ?? stack.match(/^'([^']+)'/),
  );
  if (isJust(quoted)) {
    const name = toMaybe(quoted.value[1]);
    if (isJust(name) && name.value !== "") return name.value;
  }
  const first = stack.split(",")[0]?.trim() ?? "";
  return first !== "" ? first : "Georgia";
}

type DocxImageType = "jpg" | "png" | "gif" | "bmp";

function imageTypeForMedia(mediaType: string): Maybe<DocxImageType> {
  switch (mediaType) {
    case "image/jpeg":
      return just("jpg");
    case "image/png":
      return just("png");
    case "image/gif":
      return just("gif");
    case "image/bmp":
      return just("bmp");
    default:
      return nothing;
  }
}

async function measureImage(
  bytes: Uint8Array,
  mediaType: string,
): Promise<{ width: number; height: number }> {
  try {
    const blob = new Blob([bytes as BlobPart], { type: mediaType });
    const bitmap = await createImageBitmap(blob);
    const width = bitmap.width;
    const height = bitmap.height;
    bitmap.close();
    const maxWidth = 540;
    if (width <= maxWidth) return { width, height };
    return {
      width: maxWidth,
      height: Math.max(1, Math.round((height * maxWidth) / width)),
    };
  } catch {
    return { width: 480, height: 320 };
  }
}

function makeTextRun(
  text: string,
  style: RunStyle,
  fonts: Fonts,
): ParagraphChild {
  const runOpts = {
    text,
    font: style.code === true ? fonts.mono : fonts.body,
    size:
      style.code === true
        ? 20
        : style.initial === true
          ? Math.round(24 * RAISED_INITIAL_EM)
          : 24,
    ...(style.smallCaps === true ? { smallCaps: true } : {}),
    ...(isJust(toMaybe(style.bold)) ? { bold: style.bold } : {}),
    ...(isJust(toMaybe(style.italics)) ? { italics: style.italics } : {}),
    ...(style.underline === true
      ? { underline: { type: UnderlineType.SINGLE } }
      : {}),
    ...(style.strike === true ? { strike: true } : {}),
    ...(style.highlight === true ? { highlight: HighlightColor.YELLOW } : {}),
  };
  const link = toMaybe(style.link);
  if (isJust(link) && link.value !== "") {
    return new ExternalHyperlink({
      children: [
        new TextRun({
          ...runOpts,
          color: "57534E",
          underline: { type: UnderlineType.SINGLE },
        }),
      ],
      link: link.value,
    });
  }
  return new TextRun(runOpts);
}

function inlineChildren(
  node: Node,
  style: RunStyle,
  fonts: Fonts,
): ParagraphChild[] {
  const out: ParagraphChild[] = [];
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent ?? "";
    if (text !== "") out.push(makeTextRun(text, style, fonts));
    return out;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return out;
  const el = node as HTMLElement;
  const tag = el.tagName.toLowerCase();
  const next: RunStyle = { ...style };

  if (tag === "strong" || tag === "b") next.bold = true;
  if (tag === "em" || tag === "i") next.italics = true;
  if (tag === "u") next.underline = true;
  if (tag === "s" || tag === "del" || tag === "strike") next.strike = true;
  if (tag === "mark") next.highlight = true;
  if (tag === "code") next.code = true;
  if (el.classList.contains("zw-opening-lead")) next.smallCaps = true;
  if (el.classList.contains("zw-opening-initial")) next.initial = true;
  if (tag === "a") {
    const href = trimmedMaybe(el.getAttribute("href"));
    if (isJust(href)) next.link = href.value;
  }
  if (tag === "br") {
    out.push(new TextRun({ break: 1 }));
    return out;
  }

  for (const child of Array.from(el.childNodes)) {
    out.push(...inlineChildren(child, next, fonts));
  }
  return out;
}

async function loadImageRun(
  src: string,
  rootPath: string,
  alt: string,
): Promise<Maybe<ParagraphChild>> {
  let dataUrlM: Maybe<string> = src.startsWith("data:") ? just(src) : nothing;
  if (isNothing(dataUrlM)) {
    const relativeM = assetRelativeFromSrc(src);
    if (isJust(relativeM) && isTauri()) {
      try {
        dataUrlM = just(
          await readProjectAssetDataUrl(rootPath, relativeM.value),
        );
      } catch (error) {
        console.warn(
          "DOCX export: failed to embed image",
          relativeM.value,
          error,
        );
      }
    }
  }
  if (isNothing(dataUrlM)) return nothing;
  const decodedM = decodeDataUrl(dataUrlM.value);
  if (isNothing(decodedM)) return nothing;
  const decoded = decodedM.value;
  const typeM = imageTypeForMedia(decoded.mediaType);
  if (isNothing(typeM)) {
    return just(
      new TextRun({
        text: alt !== "" ? alt : "[Image]",
        italics: true,
      }),
    );
  }
  const size = await measureImage(decoded.bytes, decoded.mediaType);
  return just(
    new ImageRun({
      type: typeM.value,
      data: decoded.bytes,
      transformation: size,
      altText: {
        title: alt !== "" ? alt : "Image",
        description: alt !== "" ? alt : "Image",
        name: alt !== "" ? alt : "image",
      },
    }),
  );
}

function spacerHeightTwips(el: Element): number {
  const style = el.getAttribute("style") ?? "";
  const match = /(?:^|;)\s*height\s*:\s*([^;]+)/i.exec(style);
  const css = (match?.[1] ?? "2em").trim();
  // Spacers are authored in em/px/rem — never fall back to inches (that
  // turns "12" / bad parses into a near-full-page gap).
  const parsed = css.match(/^([\d.]+)\s*(em|rem|px|pt)?$/i);
  if (parsed === null) return 480; // 2em
  const n = Number.parseFloat(parsed[1]!);
  if (Number.isFinite(n) === false || n <= 0) return 480;
  const unit = (parsed[2] ?? "em").toLowerCase();
  switch (unit) {
    case "px":
      return Math.max(40, Math.round((n * 1440) / 96));
    case "pt":
      return Math.max(40, Math.round(n * 20));
    case "em":
    case "rem":
    default:
      // Body Normal is 12pt → 240 twips per em.
      return Math.max(40, Math.round(n * 240));
  }
}

/**
 * Vertical gap via stacked 1em exact-height lines.
 * Large `spacing.before` values are unreliable in Pages/Word; empty paras collapse.
 */
function spacerParagraphs(heightTwips: number): Paragraph[] {
  const emTwips = 240;
  const count = Math.max(1, Math.round(heightTwips / emTwips));
  return Array.from(
    { length: count },
    () =>
      new Paragraph({
        spacing: {
          before: 0,
          after: 0,
          line: emTwips,
          lineRule: LineRuleType.EXACT,
        },
        children: [
          new TextRun({
            text: "\u00A0",
            size: 2,
            color: "FFFFFF",
          }),
        ],
      }),
  );
}

/**
 * Alignment and left indent from a `.zwdoc` block's inline style
 * (`text-align`, `margin-left` in em; see `zwdocToExportHtml`).
 */
function blockLayout(el: Element): {
  alignment?: (typeof AlignmentType)[keyof typeof AlignmentType];
  indent?: { left: number };
} {
  const style = el instanceof HTMLElement ? el.style : null;
  if (style === null) return {};
  const alignment =
    style.textAlign === "center"
      ? AlignmentType.CENTER
      : style.textAlign === "right"
        ? AlignmentType.RIGHT
        : style.textAlign === "justify"
          ? AlignmentType.JUSTIFIED
          : null;
  const indentEm = /^([\d.]+)em$/.exec(style.marginLeft.trim());
  const indentTwips =
    indentEm === null
      ? 0
      : Math.round(Number.parseFloat(indentEm[1] ?? "0") * 240);
  return {
    ...(alignment === null ? {} : { alignment }),
    ...(indentTwips > 0 ? { indent: { left: indentTwips } } : {}),
  };
}

/** Whether `el` follows another paragraph (not a scene break). */
function followsParagraph(el: Element): boolean {
  const previous = el.previousElementSibling;
  return (
    previous !== null &&
    previous.tagName.toLowerCase() === "p" &&
    previous.classList.contains("zw-scene-break") === false
  );
}

/**
 * Spacing and indent for a body paragraph under Styling ▸ Paragraphs, the
 * same rules as `chapterParagraphCss`: "indented" sets paragraphs together
 * and indents the first line, except a chapter opening, a flush first
 * paragraph, centred / right-aligned paragraphs, images and (with
 * "sceneFlush") one that doesn't follow a paragraph; "spaced" leaves a gap after each.
 */
function paragraphSetting(
  el: Element,
  layout: ReturnType<typeof blockLayout>,
  formatting: ZwParagraphFormatting,
): {
  spacing: { after: number };
  indent?: { left?: number; firstLine?: number };
} {
  const left =
    typeof layout.indent === "undefined" ? {} : { indent: layout.indent };
  switch (formatting.style) {
    case "spaced":
      return { spacing: { after: 160 }, ...left };
    case "indented": {
      const firstLine =
        el.classList.contains("zw-chapter-opening") === false &&
        el.classList.contains("zw-flush-first") === false &&
        layout.alignment !== AlignmentType.CENTER &&
        layout.alignment !== AlignmentType.RIGHT &&
        el.querySelector("img") === null &&
        (formatting.firstParagraph !== "sceneFlush" || followsParagraph(el));
      return firstLine
        ? {
            spacing: { after: 0 },
            indent: {
              ...layout.indent,
              firstLine: Math.round(PARAGRAPH_INDENT_EM * 240),
            },
          }
        : { spacing: { after: 0 }, ...left };
    }
    default:
      return assertNever(formatting.style);
  }
}

/** Inline runs for a paragraph. Editor images are inline nodes inside a
 * paragraph, so `img` children become image runs here. */
async function paragraphChildren(
  el: Element,
  rootPath: string,
  fonts: Fonts,
): Promise<ParagraphChild[]> {
  if (el.querySelector("img") === null) return inlineChildren(el, {}, fonts);
  const out: ParagraphChild[] = [];
  for (const child of Array.from(el.childNodes)) {
    if (child instanceof Element && child.tagName.toLowerCase() === "img") {
      const imageM = await loadImageRun(
        child.getAttribute("src") ?? "",
        rootPath,
        child.getAttribute("alt") ?? "",
      );
      if (isJust(imageM)) out.push(imageM.value);
      continue;
    }
    out.push(...inlineChildren(child, {}, fonts));
  }
  return out;
}

async function blockToParagraphs(
  el: Element,
  rootPath: string,
  fonts: Fonts,
  paragraphFormatting: ZwParagraphFormatting,
): Promise<Paragraph[]> {
  const tag = el.tagName.toLowerCase();

  if (tag === "div" && el.classList.contains("zw-export-spacer")) {
    return spacerParagraphs(spacerHeightTwips(el));
  }

  if (tag === "h1" || tag === "h2" || tag === "h3" || tag === "h4") {
    const level =
      tag === "h1"
        ? HeadingLevel.HEADING_1
        : tag === "h2"
          ? HeadingLevel.HEADING_2
          : tag === "h3"
            ? HeadingLevel.HEADING_3
            : HeadingLevel.HEADING_4;
    const outlineLevel =
      tag === "h1" ? 0 : tag === "h2" ? 1 : tag === "h3" ? 2 : 3;
    return [
      new Paragraph({
        heading: level,
        outlineLevel,
        ...blockLayout(el),
        spacing: { before: 240, after: 120 },
        children: inlineChildren(el, {}, fonts),
      }),
    ];
  }

  if (tag === "p" && el.classList.contains("zw-scene-break")) {
    return [sceneBreakParagraph(el.textContent ?? "", fonts)];
  }

  if (tag === "p") {
    const layout = blockLayout(el);
    return [
      new Paragraph({
        style: "Normal",
        ...layout,
        ...paragraphSetting(el, layout, paragraphFormatting),
        children: await paragraphChildren(el, rootPath, fonts),
      }),
    ];
  }

  if (tag === "blockquote") {
    const kids = Array.from(el.children);
    if (kids.length === 0) {
      return [
        new Paragraph({
          style: "Normal",
          spacing: { after: 120 },
          indent: { left: 720 },
          children: inlineChildren(el, { italics: true }, fonts),
        }),
      ];
    }
    const out: Paragraph[] = [];
    for (const child of kids) {
      out.push(
        new Paragraph({
          style: "Normal",
          spacing: { after: 120 },
          indent: { left: 720 },
          children: inlineChildren(child, { italics: true }, fonts),
        }),
      );
    }
    return out;
  }

  if (tag === "ul" || tag === "ol") {
    const ref = tag === "ul" ? "bullets" : "numbers";
    const out: Paragraph[] = [];
    for (const li of Array.from(el.children)) {
      if (li.tagName.toLowerCase() !== "li") continue;
      const nestedLists = Array.from(li.children).filter((child) =>
        /^(UL|OL)$/i.test(child.tagName),
      );
      const firstP = toMaybe(
        Array.from(li.children).find(
          (child) => child.tagName.toLowerCase() === "p",
        ),
      );

      let children: ParagraphChild[];
      if (isJust(firstP)) {
        children = inlineChildren(firstP.value, {}, fonts);
      } else {
        const direct = Array.from(li.childNodes)
          .filter((n) => n.nodeType === Node.TEXT_NODE)
          .map((n) => n.textContent ?? "")
          .join("")
          .trim();
        if (direct !== "") {
          children = [makeTextRun(direct, {}, fonts)];
        } else if (
          nestedLists.length !== 0 &&
          (li.textContent?.trim() ?? "") === ""
        ) {
          for (const nested of nestedLists) {
            out.push(
              ...(await blockToParagraphs(
                nested,
                rootPath,
                fonts,
                paragraphFormatting,
              )),
            );
          }
          continue;
        } else {
          children = inlineChildren(li, {}, fonts);
        }
      }

      out.push(
        new Paragraph({
          style: "Normal",
          numbering: { reference: ref, level: 0 },
          spacing: { after: 80 },
          children,
        }),
      );
      for (const nested of nestedLists) {
        out.push(
          ...(await blockToParagraphs(
            nested,
            rootPath,
            fonts,
            paragraphFormatting,
          )),
        );
      }
    }
    return out;
  }

  if (tag === "pre") {
    return [
      new Paragraph({
        style: "Normal",
        spacing: { before: 120, after: 120 },
        children: [
          new TextRun({
            text: el.textContent ?? "",
            font: fonts.mono,
            size: 18,
          }),
        ],
      }),
    ];
  }

  if (tag === "hr") {
    return [
      new Paragraph({
        style: "Normal",
        spacing: { before: 200, after: 200 },
        border: {
          bottom: {
            style: BorderStyle.SINGLE,
            size: 6,
            color: "D6D3D1",
            space: 1,
          },
        },
        children: [],
      }),
    ];
  }

  if (tag === "img") {
    const src = el.getAttribute("src") ?? "";
    const alt = el.getAttribute("alt") ?? "";
    const imageM = await loadImageRun(src, rootPath, alt);
    if (isNothing(imageM)) return [];
    return [
      new Paragraph({
        style: "Normal",
        spacing: { before: 120, after: 120 },
        children: [imageM.value],
      }),
    ];
  }

  const out: Paragraph[] = [];
  if (el.children.length !== 0) {
    for (const child of Array.from(el.children)) {
      out.push(
        ...(await blockToParagraphs(
          child,
          rootPath,
          fonts,
          paragraphFormatting,
        )),
      );
    }
  } else {
    const children = inlineChildren(el, {}, fonts);
    if (children.length !== 0) {
      out.push(
        new Paragraph({
          style: "Normal",
          spacing: { after: 160 },
          children,
        }),
      );
    }
  }
  return out;
}

async function htmlFragmentToParagraphs(
  html: string,
  rootPath: string,
  fonts: Fonts,
  paragraphFormatting: ZwParagraphFormatting,
  options?: { center?: boolean; italic?: boolean },
): Promise<Paragraph[]> {
  const parsed = new DOMParser().parseFromString(
    `<div id="zw-root">${html}</div>`,
    "text/html",
  );
  const root = parsed.getElementById("zw-root");
  if (root === null) return [];

  const paragraphs: Paragraph[] = [];
  const sourceNodes =
    root.children.length !== 0 ? Array.from(root.children) : [root];
  const center = options?.center === true;
  const italic = options?.italic === true;

  for (const child of sourceNodes) {
    const parts = await blockToParagraphs(
      child,
      rootPath,
      fonts,
      paragraphFormatting,
    );
    for (const part of parts) {
      if (center || italic) {
        paragraphs.push(
          new Paragraph({
            style: "Normal",
            ...(center ? { alignment: AlignmentType.CENTER } : {}),
            spacing: { before: 120, after: 160 },
            children: inlineChildren(
              child,
              { ...(italic ? { italics: true } : {}) },
              fonts,
            ),
          }),
        );
      } else {
        paragraphs.push(part);
      }
    }
  }
  return paragraphs;
}

function titlePageParagraphs(
  title: string,
  author: string,
  subtitle: Maybe<string>,
  fonts: Fonts,
): Paragraph[] {
  const parts: Paragraph[] = [
    new Paragraph({
      style: "Normal",
      alignment: AlignmentType.CENTER,
      spacing: { before: 1200, after: 200 },
      children: [
        new TextRun({
          text: title,
          bold: true,
          font: fonts.heading,
          size: 48,
        }),
      ],
    }),
  ];
  const subtitleTrimmed = isJust(subtitle) ? subtitle.value.trim() : "";
  if (subtitleTrimmed !== "") {
    parts.push(
      new Paragraph({
        style: "Normal",
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
        children: [
          new TextRun({
            text: subtitleTrimmed,
            italics: true,
            font: fonts.body,
            size: 28,
          }),
        ],
      }),
    );
  }
  const authorTrimmed = author.trim();
  if (authorTrimmed !== "") {
    parts.push(
      new Paragraph({
        style: "Normal",
        alignment: AlignmentType.CENTER,
        spacing: { before: 400, after: 200 },
        children: [
          new TextRun({
            text: authorTrimmed,
            font: fonts.body,
            size: 26,
          }),
        ],
      }),
    );
  }
  return parts;
}

function chapterTitleAlignment(
  alignment: ZwChapterHeadingStyle["alignment"],
): (typeof AlignmentType)[keyof typeof AlignmentType] {
  switch (alignment) {
    case "left":
      return AlignmentType.LEFT;
    case "center":
      return AlignmentType.CENTER;
    case "right":
      return AlignmentType.RIGHT;
    default:
      return assertNever(alignment);
  }
}

function chapterTitleTextParagraph(
  text: string,
  fonts: Fonts,
  alignment: (typeof AlignmentType)[keyof typeof AlignmentType],
  scale: number,
  before: number,
  after: number,
): Paragraph {
  return new Paragraph({
    style: "Heading1",
    alignment,
    spacing: { before, after },
    children: text.split("\n").map(
      (line, index) =>
        new TextRun({
          text: line,
          break: index === 0 ? 0 : 1,
          bold: true,
          font: fonts.heading,
          size: chapterTitleDocxHalfPoints(scale),
        }),
    ),
  });
}

async function chapterTitleParagraphs(
  text: string,
  fonts: Fonts,
  style: ZwChapterHeadingStyle,
  rootPath: string,
): Promise<Paragraph[]> {
  const alignment = chapterTitleAlignment(style.alignment);
  const before = chapterTitleSpacingTwips(style.spaceBefore);
  const after = chapterTitleSpacingTwips(style.spaceAfter);
  const titleAlone = (): Paragraph =>
    chapterTitleTextParagraph(
      text,
      fonts,
      alignment,
      style.scale,
      before,
      after,
    );

  const imagePath = style.image.trim();
  if (imagePath.length === 0) {
    return [titleAlone()];
  }

  const imageRun = await loadImageRun(imagePath, rootPath, "");
  if (isNothing(imageRun)) {
    return [titleAlone()];
  }
  const imageParagraph = new Paragraph({
    style: "Normal",
    alignment,
    spacing: {
      before: style.imagePosition === "above" ? before : 0,
      after: style.imagePosition === "below" ? after : 120,
    },
    children: [imageRun.value],
  });

  if (style.imagePosition === "below") {
    return [
      chapterTitleTextParagraph(
        text,
        fonts,
        alignment,
        style.scale,
        before,
        120,
      ),
      imageParagraph,
    ];
  }

  return [
    imageParagraph,
    chapterTitleTextParagraph(text, fonts, alignment, style.scale, 0, after),
  ];
}

function sceneBreakParagraph(text: string, fonts: Fonts): Paragraph {
  return new Paragraph({
    style: "Normal",
    alignment: AlignmentType.CENTER,
    spacing: { before: 300, after: 300 },
    children:
      text !== "" ? [new TextRun({ text, font: fonts.body, size: 22 })] : [],
  });
}

function pageBreak(): Paragraph {
  return new Paragraph({ style: "Normal", children: [new PageBreak()] });
}

const MARGIN_TWIP = 1080; // 0.75in — matches PDF @page margins
const MARGIN_BOTTOM_TWIP = 1440; // 1in
const HEADER_FROM_EDGE = 576; // 0.4in
const FOOTER_FROM_EDGE = 720; // 0.5in
const CHROME_SIZE = 18; // 9pt

function emptyHeader(): Header {
  return new Header({
    children: [new Paragraph({ children: [new TextRun({ text: "" })] })],
  });
}

function emptyFooter(): Footer {
  return new Footer({
    children: [new Paragraph({ children: [new TextRun({ text: "" })] })],
  });
}

function chromeText(text: string, fonts: Fonts, italics = false): TextRun {
  return new TextRun({
    text,
    font: fonts.body,
    size: CHROME_SIZE,
    italics,
    color: "44403C",
  });
}

/**
 * PageNumber.CURRENT is the string "CURRENT" — must use `children: […]`,
 * not `{ text: "CURRENT" }`, or the PAGE field never appears.
 */
function chromeFolio(fonts: Fonts): TextRun {
  return new TextRun({
    children: [PageNumber.CURRENT],
    font: fonts.body,
    size: CHROME_SIZE,
    color: "44403C",
  });
}

/**
 * Simple running head/footer for maximum Word + Pages compatibility.
 * Avoid even/odd + title-page variants — Pages often drops those entirely.
 */
function buildBodyChrome(
  title: string,
  author: string,
  chrome: ZwPageChrome,
  fonts: Fonts,
  contentWidth: number,
): Pick<ISectionOptions, "headers" | "footers"> {
  const folioInHeader = chrome.pageNumber === "header";
  const folioInFooter = chrome.pageNumber === "footer";

  const headLeftParts: string[] = [];
  const titleTrimmed = title.trim();
  const authorTrimmed = author.trim();
  if (chrome.showTitle === true && titleTrimmed !== "") {
    headLeftParts.push(titleTrimmed);
  }
  if (chrome.showAuthor === true && authorTrimmed !== "") {
    headLeftParts.push(authorTrimmed);
  }
  const headLeft = headLeftParts.join("  ·  ");

  const headerChildren: ParagraphChild[] = [];
  if (headLeft !== "") headerChildren.push(chromeText(headLeft, fonts, true));
  if (folioInHeader) {
    if (headerChildren.length !== 0) {
      headerChildren.push(new TextRun({ children: [new Tab()] }));
    }
    headerChildren.push(chromeFolio(fonts));
  }

  const footerChildren: ParagraphChild[] = [];
  if (folioInFooter) {
    footerChildren.push(new TextRun({ children: [new Tab()] }));
    footerChildren.push(chromeFolio(fonts));
  }

  const hasHeader = headerChildren.length !== 0;
  const hasFooter = footerChildren.length !== 0;

  return {
    headers: {
      default: hasHeader
        ? new Header({
            children: [
              new Paragraph({
                tabStops: [{ type: TabStopType.RIGHT, position: contentWidth }],
                children: headerChildren,
              }),
            ],
          })
        : emptyHeader(),
    },
    footers: {
      default: hasFooter
        ? new Footer({
            children: [
              new Paragraph({
                tabStops: [{ type: TabStopType.RIGHT, position: contentWidth }],
                children: footerChildren,
              }),
            ],
          })
        : emptyFooter(),
    },
  };
}

/**
 * Build a DOCX package as bytes from the project book.
 */
function docxExportTitle(input: BuildDocxInput): string {
  const trimmed = input.title.trim();
  if (trimmed !== "") return trimmed;
  const fromChapter =
    chaptersFromBlocks(input.blocks)[0]?.name.replace(
      /\.(md|markdown|mdown)$/i,
      "",
    ) ?? "";
  return fromChapter !== "" ? fromChapter : "Untitled";
}

/** Display size for a 300 DPI (or caller DPI) export PNG in Word's 96 DPI px. */
function mapImageDisplaySize(
  image: MapExportImage,
  maxWidthPx: number,
  dpi: number = DEFAULT_MAP_EXPORT_DPI,
): { width: number; height: number } {
  let width = Math.max(1, Math.round((image.widthPx / dpi) * 96));
  let height = Math.max(1, Math.round((image.heightPx / dpi) * 96));
  if (width > maxWidthPx) {
    height = Math.max(1, Math.round((height * maxWidthPx) / width));
    width = maxWidthPx;
  }
  return { width, height };
}

function mapImageParagraph(
  image: MapExportImage,
  alt: string,
  maxWidthPx: number,
): Paragraph {
  const size = mapImageDisplaySize(image, maxWidthPx);
  const label = alt !== "" ? alt : "Map";
  return new Paragraph({
    style: "Normal",
    alignment: AlignmentType.CENTER,
    spacing: { before: 0, after: 0 },
    children: [
      new ImageRun({
        type: "png",
        data: image.bytes,
        transformation: size,
        altText: {
          title: label,
          description: label,
          name: label,
        },
      }),
    ],
  });
}

export async function buildBookDocx(
  input: BuildDocxInput,
): Promise<Uint8Array> {
  const title = docxExportTitle(input);
  const author = input.author.trim();
  const metaTitleTrimmed = input.metadata?.title?.trim() ?? "";
  const metaAuthorTrimmed = input.metadata?.author?.trim() ?? "";
  const meta: ZwMetadata = {
    ...defaultZwMetadata(),
    ...input.metadata,
    title: metaTitleTrimmed !== "" ? metaTitleTrimmed : title,
    subtitle: input.metadata?.subtitle ?? input.subtitle ?? "",
    author: metaAuthorTrimmed !== "" ? metaAuthorTrimmed : author,
  };

  const fonts: Fonts = {
    body: primaryFontName(input.bodyFontId),
    heading: primaryFontName(input.headingFontId),
    mono: "Courier New",
  };

  const pageSize = printPageSizeFor(input.printSize ?? "a5");
  const pageWidth = cssLengthToTwip(pageSize.width);
  const pageHeight = cssLengthToTwip(pageSize.height);
  const contentWidth = Math.max(720, pageWidth - MARGIN_TWIP * 2);
  const chrome = input.pageChrome ?? defaultZwPageChrome();

  const pageProperties = {
    size: { width: pageWidth, height: pageHeight },
    margin: {
      top: MARGIN_TWIP,
      right: MARGIN_TWIP,
      bottom: MARGIN_BOTTOM_TWIP,
      left: MARGIN_TWIP,
      header: HEADER_FROM_EDGE,
      footer: FOOTER_FROM_EDGE,
    },
  };

  const frontMatter: Paragraph[] = [];

  const coverRelative = meta.coverImage.trim();
  if (coverRelative !== "" && isTauri()) {
    try {
      const dataUrl = await readProjectAssetDataUrl(
        input.rootPath,
        coverRelative,
      );
      const imageM = await loadImageRun(dataUrl, input.rootPath, title);
      if (isJust(imageM)) {
        frontMatter.push(
          new Paragraph({
            style: "Normal",
            alignment: AlignmentType.CENTER,
            spacing: { after: 400 },
            children: [imageM.value],
          }),
        );
        frontMatter.push(pageBreak());
      }
    } catch (error) {
      console.warn("DOCX export: failed to embed cover", error);
    }
  }

  frontMatter.push(
    ...titlePageParagraphs(
      title,
      author,
      trimmedMaybe(
        meta.subtitle !== "" ? meta.subtitle : (input.subtitle ?? ""),
      ),
      fonts,
    ),
  );

  const copyrightHtml = buildCopyrightPageHtml(
    input.copyrightPage ?? defaultZwCopyrightPage(),
    meta,
  );
  if (isJust(copyrightHtml)) {
    frontMatter.push(pageBreak());
    frontMatter.push(
      ...(await htmlFragmentToParagraphs(
        copyrightHtml.value,
        input.rootPath,
        fonts,
        FRONT_MATTER_PARAGRAPHS,
      )),
    );
  }

  const dedicationHtml = buildDedicationPageHtml(
    input.dedicationPage ?? defaultZwDedicationPage(),
  );
  if (isJust(dedicationHtml)) {
    frontMatter.push(pageBreak());
    frontMatter.push(
      ...(await htmlFragmentToParagraphs(
        dedicationHtml.value,
        input.rootPath,
        fonts,
        FRONT_MATTER_PARAGRAPHS,
        { center: true, italic: true },
      )),
    );
  }

  const sections: ISectionOptions[] = [
    {
      properties: {
        type: SectionType.NEXT_PAGE,
        page: pageProperties,
      },
      headers: {
        default: emptyHeader(),
      },
      footers: {
        default: emptyFooter(),
      },
      children: frontMatter,
    },
  ];

  // Body sections keep running heads/folios. Spreads use EVEN_PAGE so verso
  // lands on the left in facing-page viewers (matches PDF break-before:left).
  const headingStyle = resolveChapterHeadingStyle(
    toMaybe(input.chapterHeadingStyle),
  );
  const bodyChrome = buildBodyChrome(
    title,
    author,
    chrome,
    fonts,
    contentWidth,
  );
  // Word ImageRun transformation is CSS px (~96 DPI). Content width in twips
  // → px: twip / 20 = pt, pt * 96/72 = px → twip * 96 / 1440.
  const maxMapWidthPx = Math.max(120, Math.round((contentWidth * 96) / 1440));
  let bodyChildren: Paragraph[] = [];
  let firstBodySection = true;

  const flushBodySection = (): void => {
    if (bodyChildren.length === 0) return;
    sections.push({
      properties: {
        // First chapter opens on a recto, matching PDF export.
        type: firstBodySection ? SectionType.ODD_PAGE : SectionType.NEXT_PAGE,
        page: pageProperties,
      },
      ...bodyChrome,
      children: bodyChildren,
    });
    bodyChildren = [];
    firstBodySection = false;
  };

  for (const block of input.blocks) {
    switch (block.type) {
      case "chapter": {
        if (bodyChildren.length !== 0) bodyChildren.push(pageBreak());
        bodyChildren.push(
          ...(await chapterTitleParagraphs(
            block.name,
            fonts,
            headingStyle,
            input.rootPath,
          )),
        );
        const parts = await htmlFragmentToParagraphs(
          block.html,
          input.rootPath,
          fonts,
          input.paragraphFormatting,
        );
        if (parts.length !== 0) bodyChildren.push(...parts);
        else
          bodyChildren.push(new Paragraph({ style: "Normal", children: [] }));
        break;
      }
      case "map": {
        const print = mapImagesForPrint(block.prepared);
        const alt = block.prepared.title;
        if (print.kind === "spread") {
          flushBodySection();
          sections.push({
            properties: {
              type: SectionType.EVEN_PAGE,
              page: pageProperties,
            },
            ...bodyChrome,
            children: [
              mapImageParagraph(print.verso, alt, maxMapWidthPx),
              pageBreak(),
              mapImageParagraph(print.recto, "", maxMapWidthPx),
            ],
          });
          firstBodySection = false;
        } else {
          // Single maps are stored unfitted (PDF uses CSS object-fit); bake fit for Word.
          const fitted = await fitPreparedMapForPrintBox(
            block.prepared,
            input.printSize ?? "a5",
          );
          if (bodyChildren.length !== 0) bodyChildren.push(pageBreak());
          bodyChildren.push(mapImageParagraph(fitted, alt, maxMapWidthPx));
        }
        break;
      }
      default:
        assertNever(block);
    }
  }

  flushBodySection();

  const doc = new Document({
    ...(author !== "" ? { creator: author } : {}),
    title,
    ...(meta.description.trim() !== ""
      ? { description: meta.description.trim() }
      : {}),
    // Keep false: even/odd + title-page headers are often dropped by Pages.
    evenAndOddHeaderAndFooters: false,
    styles: {
      default: {
        document: {
          run: {
            font: fonts.body,
            size: 24,
          },
          paragraph: {
            spacing: { after: 120, line: 276 },
          },
        },
        // Customize built-ins — do NOT re-declare Heading1 via paragraphStyles
        // (that creates a duplicate style and confuses Pages/Word).
        heading1: {
          run: { font: fonts.heading, size: 36, bold: true, color: "1C1917" },
          paragraph: {
            spacing: { before: 360, after: 160 },
            outlineLevel: 0,
          },
        },
        heading2: {
          run: { font: fonts.heading, size: 30, bold: true, color: "1C1917" },
          paragraph: {
            spacing: { before: 280, after: 120 },
            outlineLevel: 1,
          },
        },
        heading3: {
          run: { font: fonts.heading, size: 26, bold: true, color: "1C1917" },
          paragraph: {
            spacing: { before: 240, after: 100 },
            outlineLevel: 2,
          },
        },
        heading4: {
          run: { font: fonts.heading, size: 24, bold: true, color: "1C1917" },
          paragraph: {
            spacing: { before: 200, after: 100 },
            outlineLevel: 3,
          },
        },
      },
      paragraphStyles: [
        {
          id: "Normal",
          name: "Normal",
          run: {
            font: fonts.body,
            size: 24,
          },
          paragraph: {
            spacing: { after: 160, line: 276 },
          },
        },
      ],
    },
    numbering: {
      config: [
        {
          reference: "bullets",
          levels: [
            {
              level: 0,
              format: LevelFormat.BULLET,
              text: "•",
              alignment: AlignmentType.LEFT,
              style: {
                paragraph: {
                  indent: { left: 720, hanging: 360 },
                },
              },
            },
          ],
        },
        {
          reference: "numbers",
          levels: [
            {
              level: 0,
              format: LevelFormat.DECIMAL,
              text: "%1.",
              alignment: AlignmentType.LEFT,
              style: {
                paragraph: {
                  indent: { left: 720, hanging: 360 },
                },
              },
            },
          ],
        },
      ],
    },
    sections,
  });

  const buffer = await Packer.toArrayBuffer(doc);
  return new Uint8Array(buffer);
}
